import React, { useState, useEffect } from 'react';
import styles from './TemplateManager.module.scss';
import api from "../../services/api";

const TEAMS = [
    'Дизайн Чио Чио',
    'IT ТП',
];

const EMPTY_TEMPLATE = {
    id: null,
    title: '',
    description: '',
    type: 'general',
    team: '',
    taskTitleAfterApply: '',
    taskStatusAfterApplyId: '',
};

const TemplateManager = () => {
    const [templates, setTemplates] = useState([]); // Список шаблонов
    const [statuses, setStatuses] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false); // Состояние модального окна
    const [currentTemplate, setCurrentTemplate] = useState(EMPTY_TEMPLATE);
    const [isEditing, setIsEditing] = useState(false); // Режим редактирования

    // Загрузка шаблонов с сервера
    useEffect(() => {
        fetchTemplates();
        fetchStatuses();
    }, []);

    const fetchTemplates = async () => {
        try {
            const response = await api.get('/api/templates/');
            setTemplates(response.data);
        } catch (error) {
            console.error('Ошибка при загрузке шаблонов:', error);
        }
    };

    const fetchStatuses = async () => {
        try {
            const response = await api.get('/api/templates/statuses');
            setStatuses(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Ошибка при загрузке статусов:', error);
        }
    };

    // Открытие модального окна для добавления или редактирования
    const openModal = (template = EMPTY_TEMPLATE) => {
        setCurrentTemplate({
            ...EMPTY_TEMPLATE,
            ...template,
            taskTitleAfterApply: template.taskTitleAfterApply || '',
            taskStatusAfterApplyId: template.taskStatusAfterApply?.id || template.taskStatusAfterApplyId || '',
        });
        setIsEditing(template.id !== null);
        setIsModalOpen(true);
    };

    // Закрытие модального окна
    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentTemplate(EMPTY_TEMPLATE);
    };

    // Обработка изменения полей формы
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentTemplate({ ...currentTemplate, [name]: value });
    };

    // Сохранение шаблона (добавление или редактирование)
    const saveTemplate = async () => {
        try {
            const templateData = {
                title: currentTemplate.title,
                description: currentTemplate.description,
                type: currentTemplate.type,
                team: currentTemplate.team,
                taskTitleAfterApply: currentTemplate.taskTitleAfterApply,
                taskStatusAfterApplyId: currentTemplate.taskStatusAfterApplyId || null,
            };

            if (isEditing) {
                await api.put(`/api/templates/${currentTemplate.id}`, templateData);
            } else {
                await api.post('/api/templates/', templateData);
            }

            fetchTemplates(); // Обновляем список
            closeModal(); // Закрываем модальное окно
        } catch (error) {
            console.error('Ошибка при сохранении шаблона:', error);
        }
    };

    // Удаление шаблона
    const deleteTemplate = async (id) => {
        try {
            await api.delete(`/api/templates/${id}`);
            fetchTemplates(); // Обновляем список
        } catch (error) {
            console.error('Ошибка при удалении шаблона:', error);
        }
    };

    return (
        <div className={styles.templateManager}>
            <h2>Управление шаблонами</h2>
            <button onClick={() => openModal()} className={styles.addButton}>
                Добавить шаблон
            </button>

            {/* Список шаблонов */}
            <ul className={styles.templateList}>
                {templates.map((template) => (
                    <li key={template.id} className={styles.templateItem}>
                        <div className={styles.templateContent}>
                            <h3>{template.title}</h3>
                            <p>{template.description}</p>
                            <p><strong>Тип:</strong> {template.type === 'general' ? 'Общие' : 'Личные'}</p>
                            {template.team && <p><strong>Команда:</strong> {template.team}</p>}
                            {(template.taskTitleAfterApply || template.taskStatusAfterApply) && (
                                <div className={styles.automationInfo}>
                                    {template.taskTitleAfterApply && (
                                        <p><strong>Новое название заявки:</strong> {template.taskTitleAfterApply}</p>
                                    )}
                                    {template.taskStatusAfterApply && (
                                        <p><strong>Статус после применения:</strong> {template.taskStatusAfterApply.title}</p>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className={styles.templateActions}>
                            <button onClick={() => openModal(template)} className={styles.editButton}>
                                ✏️
                            </button>
                            <button onClick={() => deleteTemplate(template.id)} className={styles.deleteButton}>
                                🗑️
                            </button>
                        </div>
                    </li>
                ))}
            </ul>

            {/* Модальное окно для добавления/редактирования */}
            {isModalOpen && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <h3>{isEditing ? 'Редактировать шаблон' : 'Добавить шаблон'}</h3>
                        <form>
                            <div className={styles.formGroup}>
                                <label>Заголовок</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={currentTemplate.title}
                                    onChange={handleInputChange}
                                    className={styles.inputField}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Описание</label>
                                <textarea
                                    name="description"
                                    value={currentTemplate.description}
                                    onChange={handleInputChange}
                                    className={styles.textareaField}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Тип шаблона</label>
                                <select
                                    name="type"
                                    value={currentTemplate.type}
                                    onChange={handleInputChange}
                                    className={styles.inputField}
                                >
                                    <option value="general">Общие</option>
                                    <option value="personal">Личные</option>
                                </select>
                            </div>
                            <div className={styles.formGroup}>
                                <label>Команда (отдел)</label>
                                <select
                                    name="team"
                                    value={currentTemplate.team}
                                    onChange={handleInputChange}
                                    className={styles.inputField}
                                >
                                    <option value="">Выберите команду</option>
                                    {TEAMS.map((team, index) => (
                                        <option key={index} value={team}>
                                            {team}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.formGroup}>
                                <label>Новое название заявки</label>
                                <input
                                    type="text"
                                    name="taskTitleAfterApply"
                                    value={currentTemplate.taskTitleAfterApply}
                                    onChange={handleInputChange}
                                    className={styles.inputField}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Статус после применения шаблона</label>
                                <select
                                    name="taskStatusAfterApplyId"
                                    value={currentTemplate.taskStatusAfterApplyId}
                                    onChange={handleInputChange}
                                    className={styles.inputField}
                                >
                                    <option value="">Не менять статус</option>
                                    {statuses.map((status) => (
                                        <option key={status.id} value={status.id}>
                                            {status.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </form>
                        <div className={styles.modalActions}>
                            <button onClick={saveTemplate} className={styles.saveButton}>
                                {isEditing ? 'Сохранить' : 'Добавить'}
                            </button>
                            <button onClick={closeModal} className={styles.cancelButton}>
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TemplateManager;
